import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL ↔ filter state синхронизация для каталогов с multi-value array фильтрами.
 *
 * Источник истины — URL search params: `?fieldA=val1&fieldA=val2&fieldB=val3`.
 * Это даёт deep-link sharing и сохранение через «Назад» (P-036).
 *
 * Множественные значения сериализуются повторением ключа (RFC 1738) — совпадает
 * с сериализацией API-клиента (см. `client.ts buildUrl()`).
 *
 * Generic по array-полям:
 *   - Block 4: `useCatalogFilters(['colors', 'shapes'])` →
 *     `{ filters: { colors: string[], shapes: string[] }, ... }`.
 *   - Block 2 (Phase 6): `useCatalogFilters(['subcategories', 'brands', 'types'])`.
 *
 * Other URL params (cursor, kind, etc.) hook не трогает — Container управляет
 * ими отдельно через свой `useSearchParams`. Hook только для array-фильтров.
 */
export type CatalogFilters<TKey extends string> = Readonly<Record<TKey, readonly string[]>>;

export interface UseCatalogFiltersResult<TKey extends string> {
  filters: CatalogFilters<TKey>;
  hasFilters: boolean;
  apply: (next: CatalogFilters<TKey>) => void;
  reset: () => void;
}

export function useCatalogFilters<TKey extends string>(
  arrayFields: readonly TKey[],
): UseCatalogFiltersResult<TKey> {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<CatalogFilters<TKey>>(() => {
    const out = {} as Record<TKey, readonly string[]>;
    for (const field of arrayFields) {
      out[field] = searchParams.getAll(field);
    }
    return out;
    // arrayFields treated as stable — caller передаёт literal array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, arrayFields.join('|')]);

  const hasFilters = useMemo(
    () => arrayFields.some((field) => (filters[field]?.length ?? 0) > 0),
    [filters, arrayFields],
  );

  const apply = useCallback(
    (next: CatalogFilters<TKey>) => {
      // Функциональный апдейтер: пересобираем из prev, чтобы сохранить non-array
      // params (cursor, kind, step и т.п.) — хук владеет только array-полями
      // (docstring выше). `delete` перед `append` снимает прежние значения поля
      // и не даёт дублей при повторном apply.
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        for (const field of arrayFields) params.delete(field);
        for (const field of arrayFields) {
          for (const value of next[field] ?? []) {
            params.append(field, value);
          }
        }
        return params;
      });
    },
    [setSearchParams, arrayFields],
  );

  const reset = useCallback(() => {
    // Чистим только array-поля хука, non-array params остаются нетронутыми.
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      for (const field of arrayFields) params.delete(field);
      return params;
    });
  }, [setSearchParams, arrayFields]);

  return { filters, hasFilters, apply, reset };
}
